import React, { useState, useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import sfdaMedicine from "../../gql/sfdaMedicine";
import AppTable from "../../shared/AppTable";
import AppButton from "../../shared/AppButton";
import PageHeader from "../../shared/PageHeader";
import Pagination from "../../components/Pagination";
import ConformationModal from "../../components/ConformationModal";
import TableSkeletonLoader from "../../common/TableLoading";
import useSfdaTableColumns from "./components/SfdaTableColumns";

function SfdaMedicineList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const columns = useSfdaTableColumns();

  const [limit] = useState(15);
  const [pageNo, setPageNo] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [search, setSearch] = useState("");
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  const { loading, error, data, refetch } = useQuery(
    sfdaMedicine.GET_SFDA_MEDICINES,
    {
      variables: { page: pageNo, limit: limit, search: search },
      fetchPolicy: "network-only",
    }
  );

  const [deleteMedicine, { loading: deleting }] = useMutation(
    sfdaMedicine.DELETE_SFDA_MEDICINE,
    {
      onCompleted: () => {
        toast.success(t("Successfully Deleted!"));
        setShowDeleteAlert(false);
        refetch();
      },
      onError: (error) => {
        toast.error(error.message);
        setShowDeleteAlert(false);
      },
    }
  );

  useEffect(() => {
    if (error) {
      toast.error(error.message);
    }
  }, [error]);

  const onPageChange = (currentPage) => {
    setPageNo(currentPage);
  };

  const onClickView = (item) => {
    navigate(`/sfda-medicine/detail/${item.id}`);
  };

  const onClickEdit = (item) => {
    navigate(`/sfda-medicine/form/${item.id}`);
  };

  const onClickDelete = (item) => {
    setCurrentItem(item);
    setShowDeleteAlert(true);
  };

  const handleDelete = () => {
    deleteMedicine({ variables: { id: currentItem.id } });
  };

  // Debounce search to prevent immediate API requests on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchTerm);
      setPageNo(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const pageInfo = data?.getSfdaMedicines?.pageInfo;

  return (
    <div className="superGit_container ">
      <PageHeader
        title={t("SFDA Medicines")}
        subtitle={true}
        total={pageInfo?.totalCount || 0}
        entriesLabel={t("Entries")}
        containerClass="!mb-1"
      >
        <div className="relative w-full md:w-[240px]">
          <input
            type="text"
            placeholder={t("Search by name, code, register...")}
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full !h-[28px] rounded-[4px] px-2.5 pr-8 border border-gray-300 !bg-gray-100 text-[12px] text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-[#0070C2] shadow-none"
          />
          <SearchIcon className="!w-4 !h-4 text-gray-400 absolute right-2 top-1.5 pointer-events-none" />
        </div>
        <AppButton
          label={t("Create Medicine")}
          startIcon={<AddIcon />}
          onClick={() => navigate("/sfda-medicine/form")}
        />
      </PageHeader>

      <AppTable
        columns={columns}
        data={data?.getSfdaMedicines?.medicines}
        loading={loading}
        onView={onClickView}
        onEdit={onClickEdit}
        onDelete={onClickDelete}
        LoaderComponent={
          <TableSkeletonLoader columnCount={10} rowCount={10} />
        }
      />

      <div className="">
        <Pagination
          currentPage={pageNo}
          totalPages={pageInfo?.totalPages || 1}
          onPageChange={onPageChange}
        />
      </div>

      <ConformationModal
        open={showDeleteAlert}
        setOpen={setShowDeleteAlert}
        onDelete={handleDelete}
        isLoading={deleting}
      />
    </div>
  );
}

export default SfdaMedicineList;
